/**
 * Live render: KMD text → the real canvas, on every keystroke
 * (KHIYARSA-EDITOR-1 Phase 3, §B2 of the contract).
 *
 * Three things make this different from the Import dialog's preview:
 *
 * 1. It renders into the SCENE, not an offscreen `exportToCanvas` raster, via
 *    `updateScene`. It must never go through `addElementsFromPasteOrLibrary`,
 *    which re-ids every element, re-groups, randomises seeds, adopts elements
 *    into whatever frame is under the drop point, resets the selection and
 *    re-fits the viewport — all of which would fire on every character.
 *
 * 2. Undo. Every intermediate render is `CaptureUpdateAction.EVENTUALLY`,
 *    which emits an ephemeral increment and does NOT advance the store
 *    snapshot, so nothing reaches the undo stack while you type. One
 *    `IMMEDIATELY` at the commit boundary folds the whole session into a
 *    single history entry. The hazard that buys: because EVENTUALLY leaves
 *    the snapshot alone, any unrelated IMMEDIATELY fired while we are bound
 *    would swallow the pending render — which is exactly why the first
 *    foreign mutation commits and detaches.
 *
 * 3. Detach. KMD has no coordinate syntax (spec §4), so a node the user drags
 *    cannot be written back into the text. Rather than lock the elements,
 *    silently lose the edit, or keep hidden state, the session detaches: the
 *    text stops driving the canvas until the user says otherwise.
 */
import { CaptureUpdateAction } from "@excalidraw/element";
import { convertToExcalidrawElements } from "@excalidraw/element";
import { useApp } from "@excalidraw/excalidraw/components/App";
import { useCallback, useEffect, useRef, useState } from "react";

import type { ExcalidrawElement } from "@excalidraw/element/types";

import {
  detectForeignEdit,
  isLive,
  mergeIntoScene,
  snapshotFingerprints,
  stripLive,
} from "./reconcile";

import type { CompileResult } from "../types";

/** cheap families compile in single-digit ms; architecture is the outlier */
const FAST_DEBOUNCE_MS = 300;
const SLOW_DEBOUNCE_MS = 1200;
/** a compile slower than this switches the debounce to the slow cadence */
const SLOW_COMPILE_MS = 150;

export type BindState = "idle" | "bound" | "detached";

/**
 * Families the editor drives without qualification (contract §B5, Wave 1).
 * Every one of them is pinned at or near zero layout defects.
 */
const PRODUCTION_FAMILIES = [
  "flow",
  "tree",
  "mindmap",
  "timeline",
  "sequence",
  "er",
] as const;

/**
 * Families the editor renders but does NOT claim as production-ready (Q9).
 *
 * `architecture` works — the mechanism is family-agnostic — but its known
 * cross-container routing defects (stacked arrowheads on fan-in, the
 * services-zone edge bus, arrows dying at a zone border, clipped labels) are
 * visible on every keystroke rather than once after an import. It stays behind
 * an explicit staged affordance until `stress-3` and `arch-live-1` are clean in
 * LIVE renders.
 *
 * `swimlane` is not here at all: it is gated behind the cross-container routing
 * work (LAYOUT-QA-BASELINE Phase 3 item 1) and stress-2 still pins I1=1/I2=22.
 */
const EXPERIMENTAL_FAMILIES = ["architecture"] as const;

/** how the editor treats a family right now */
export type FamilyGate = "production" | "experimental" | "blocked";

export const gateFor = (family: string | null | undefined): FamilyGate => {
  if (!family) {
    return "production";
  }
  if ((PRODUCTION_FAMILIES as readonly string[]).includes(family)) {
    return "production";
  }
  if ((EXPERIMENTAL_FAMILIES as readonly string[]).includes(family)) {
    return "experimental";
  }
  return "blocked";
};

export interface LiveRenderApi {
  result: CompileResult | null;
  /** how the editor treats the document's family right now (Q9) */
  gate: FamilyGate;
  bindState: BindState;
  /** true while a compile is in flight or queued */
  pending: boolean;
  /** discard canvas edits and re-bind the text to the canvas */
  rebind: () => void;
  /** fold the session into one undo entry and stop driving the canvas */
  commit: () => void;
  /** remove everything this session rendered */
  discard: () => void;
  /** scroll the rendered diagram into view (never automatic) */
  fit: () => void;
}

export const useLiveRender = ({
  text,
  active,
}: {
  text: string;
  active: boolean;
}): LiveRenderApi => {
  const app = useApp();
  const [result, setResult] = useState<CompileResult | null>(null);
  const [bindState, setBindState] = useState<BindState>("idle");
  const [pending, setPending] = useState(false);
  /** bumped by `rebind` to force the compile effect to run again */
  const [rebindNonce, setRebindNonce] = useState(0);

  /** versions of the elements we wrote, so a user edit is detectable */
  const written = useRef<Map<string, string>>(new Map());
  const compileToken = useRef(0);
  const lastCompileMs = useRef(0);
  const bindStateRef = useRef<BindState>("idle");
  bindStateRef.current = bindState;

  const scene = useCallback(
    () => app.scene.getElementsIncludingDeleted() as ExcalidrawElement[],
    [app],
  );

  const push = useCallback(
    (elements: ExcalidrawElement[], capture: "EVENTUALLY" | "IMMEDIATELY") => {
      const instance = app as unknown as {
        updateScene: (data: {
          elements: readonly ExcalidrawElement[];
          appState?: Record<string, unknown>;
          captureUpdate?: typeof CaptureUpdateAction[keyof typeof CaptureUpdateAction];
        }) => void;
        store: { scheduleCapture: () => void };
      };

      // TRAP: `updateScene({ elements, captureUpdate: IMMEDIATELY })` does NOT
      // work with an element ARRAY. The store's `detectChangedElements` calls
      // `nextElements.get(id)`, which only a SceneElementsMap has; an array
      // throws inside the scheduled micro action, and `flushMicroActions`
      // swallows it — so the capture silently never happens. EVENTUALLY looks
      // fine only because it early-returns before reaching that code.
      //
      // The commit therefore goes through the macro path instead: schedule the
      // capture and let the App's own `componentDidUpdate` commit it with the
      // real elementsMap. That is exactly what `addElementsFromPasteOrLibrary`
      // does for its one-shot insert.
      if (capture === "IMMEDIATELY") {
        instance.store.scheduleCapture();
        // appState forces the re-render that drives componentDidUpdate → commit
        instance.updateScene({ elements, appState: {} });
      } else {
        instance.updateScene({
          elements,
          captureUpdate: CaptureUpdateAction.EVENTUALLY,
        });
      }

      written.current = snapshotFingerprints(
        app.scene.getElementsIncludingDeleted() as ExcalidrawElement[],
      );
    },
    [app],
  );

  // ---- release stale live flags on activation ---------------------------
  // "live" means "the editor is driving this RIGHT NOW", which cannot survive
  // a page load. A reload mid-session leaves the flag set in the saved scene;
  // without this, the next session adopts those elements, finds their
  // fingerprints unfamiliar, and detaches the instant the panel opens.
  useEffect(() => {
    if (!active) {
      return;
    }
    const stale = scene().filter(isLive);
    if (stale.length > 0) {
      push(stripLive(scene()), "EVENTUALLY");
      written.current = new Map();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  // ---- compile + render -------------------------------------------------
  useEffect(() => {
    if (!active) {
      return;
    }
    if (bindStateRef.current === "detached") {
      // still compile so diagnostics stay live, but do not touch the canvas
      const token = ++compileToken.current;
      const timer = window.setTimeout(async () => {
        const [{ compileKmd }, { excalidrawMeasure }] = await Promise.all([
          import(".."),
          import("../measureBrowser"),
        ]);
        const compiled = await compileKmd(text, { measure: excalidrawMeasure });
        if (token === compileToken.current) {
          setResult(compiled);
        }
      }, FAST_DEBOUNCE_MS);
      return () => window.clearTimeout(timer);
    }

    const token = ++compileToken.current;
    setPending(true);
    const delay =
      lastCompileMs.current > SLOW_COMPILE_MS
        ? SLOW_DEBOUNCE_MS
        : FAST_DEBOUNCE_MS;

    const timer = window.setTimeout(async () => {
      const started = performance.now();
      const [{ compileKmd }, { excalidrawMeasure }] = await Promise.all([
        import(".."),
        import("../measureBrowser"),
      ]);
      const compiled = await compileKmd(text, { measure: excalidrawMeasure });
      lastCompileMs.current = performance.now() - started;
      if (token !== compileToken.current) {
        return;
      }

      if (!text.trim()) {
        // an EMPTY document removes the render; an INVALID one keeps the last
        // good picture on canvas, because a document is invalid on almost
        // every keystroke and wiping it each time is unusable
        push(
          scene().filter((el) => !isLive(el)),
          "EVENTUALLY",
        );
        setBindState("idle");
        setResult(null);
        setPending(false);
        return;
      }

      // Q9: a blocked family compiles and shows diagnostics but never drives
      // the canvas — live-rendering a layout we would not show without
      // apology is worse than not rendering it
      if (gateFor(compiled.doc?.family) === "blocked") {
        setResult(compiled);
        setPending(false);
        return;
      }

      if (compiled.ok) {
        try {
          const converted = convertToExcalidrawElements(
            compiled.skeletons as any,
            { regenerateIds: false },
          ) as ExcalidrawElement[];
          const rendered = [
            ...converted,
            ...((compiled.extraElements ?? []) as ExcalidrawElement[]),
          ];
          push(mergeIntoScene(scene(), rendered), "EVENTUALLY");
          setBindState("bound");
        } catch (error) {
          console.error("KMD live render failed", error);
        }
      }
      setResult(compiled);
      setPending(false);
    }, delay);

    return () => window.clearTimeout(timer);
  }, [text, active, rebindNonce, push, scene]);

  // ---- detach on the first hand edit ------------------------------------
  useEffect(() => {
    if (!active || bindState !== "bound") {
      return;
    }
    // `onChangeEmitter` — not `scene.onUpdate`. A drag mutates elements with
    // `informMutation: false` for performance, so the Scene never announces
    // it and a node can be moved without a single scene callback firing.
    // onChangeEmitter is what backs the public `onChange` prop and fires for
    // every settled change, ours included; the version snapshot is what
    // separates the user's edits from our renders.
    const unsubscribe = (
      app as unknown as {
        onChangeEmitter: { on: (cb: () => void) => () => void };
      }
    ).onChangeEmitter.on(() => {
      if (bindStateRef.current !== "bound") {
        return;
      }
      if (detectForeignEdit(scene(), written.current)) {
        // Commit what is pending FIRST, so the user's edit and our render do
        // not end up squashed into one indistinguishable history entry.
        //
        // The live marker deliberately STAYS. Detaching means "the text has
        // stopped driving these elements", not "these elements are no longer
        // the editor's" — "Re-render from text" has to be able to find and
        // replace them. Only committing releases ownership.
        push(scene(), "IMMEDIATELY");
        setBindState("detached");
      }
    });
    return unsubscribe;
  }, [app, active, bindState, push, scene]);

  // ---- commit on close --------------------------------------------------
  useEffect(() => {
    if (active) {
      return;
    }
    if (bindStateRef.current !== "idle") {
      push(stripLive(scene()), "IMMEDIATELY");
      setBindState("idle");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  // Committing releases ownership: the marker comes off, so the diagram is
  // ordinary elements from here on and no later session can reclaim it.
  const commit = useCallback(() => {
    push(stripLive(scene()), "IMMEDIATELY");
    written.current = new Map();
    setBindState("idle");
  }, [push, scene]);

  /**
   * "Re-render from text": drop the hand-edited diagram and rebuild it.
   *
   * The detached elements still carry the live marker (detach keeps it on
   * purpose), so filtering by it is exactly how we find what to remove.
   * Going back to `idle` — rather than straight to `bound` — matters: the
   * compile effect skips the canvas while detached, so the state has to
   * change before the nonce makes it run again.
   */
  const rebind = useCallback(() => {
    push(
      scene().filter((el) => !isLive(el)),
      "EVENTUALLY",
    );
    written.current = new Map();
    lastCompileMs.current = 0;
    setBindState("idle");
    setRebindNonce((n) => n + 1);
  }, [push, scene]);

  const discard = useCallback(() => {
    push(
      scene().filter((el) => !isLive(el)),
      "IMMEDIATELY",
    );
    written.current = new Map();
    setBindState("idle");
  }, [push, scene]);

  const fit = useCallback(() => {
    const mine = scene().filter(isLive);
    if (mine.length > 0) {
      // explicit only — the live path never moves the viewport on its own
      (
        app as unknown as {
          viewport: { setViewport: (o: unknown) => void };
        }
      ).viewport.setViewport({
        target: mine,
        fit: "contain",
        animation: false,
        offsets: { ui: true },
      });
    }
  }, [app, scene]);

  return {
    result,
    gate: gateFor(result?.doc?.family),
    bindState,
    pending,
    rebind,
    commit,
    discard,
    fit,
  };
};
